<template>
    <div class="">
        <!-- The overlay uses v-show, NOT v-if, so it stays mounted the whole
             time and only toggles display. That is what lets the panel's own
             transition below actually run: a transition child only animates
             when it toggles inside an already-present parent. When the overlay
             itself was v-if, it and the panel appeared on the same tick, so the
             panel mounted straight into its final position with no slide.

             overflow-hidden while closed also keeps the off-screen panel
             (translated 100% to the right) from adding a horizontal scrollbar. -->
        <div    v-show="visible"
                class="fixed inset-0 overflow-hidden"
                :class="zIndex">
            <div class="absolute inset-0 overflow-hidden"
                    @click="closeModal">
                <!-- Anchoring. By default a right-edge slide-over: full-height,
                     pinned right, sized by `width`. With `mobile-bottom-sheet` it
                     becomes a bottom sheet BELOW sm -- pinned to the bottom, full
                     width, tall (its height comes from the panel's own dvh height
                     below, NOT from stretching to a top offset) -- and reverts to
                     the right slide-over from sm up.

                     items-end below sm sits the panel against the bottom; the panel
                     sizes itself with a dynamic-viewport height so a gap is left at
                     the top without relying on bottom+top stretch (which leaves the
                     inner h-full content unresolved on mobile Safari, the "sheet
                     comes up short" bug). -->
                <section :class="mobileBottomSheet
                             ? 'absolute inset-0 flex max-w-full items-end sm:inset-y-0 sm:left-auto sm:right-0 sm:mt-20 sm:items-stretch sm:pl-16'
                             : 'absolute inset-y-0 right-0 mt-20 flex max-w-full pl-10 sm:pl-16'">
                        <!--
                          The sliding panel. Its own transition, so it slides in
                          from the right (or UP from the bottom, when
                          mobile-bottom-sheet is on below sm) on open and back out
                          on close.

                          Kept snappy: a fast ease-out slide in (200ms) and an
                          even quicker ease-in slide out (150ms), so the panel
                          feels responsive rather than languid.

                          The transition classes are chosen by `enterFromClass` /
                          `leaveToClass` so the offscreen direction matches the
                          anchor: translate-x-full for the right slide-over,
                          panel-offscreen-bottom for the bottom sheet (a scoped
                          class that translates Y below sm and X from sm up, so the
                          same panel animates the right way at both breakpoints).
                        -->
                        <transition
                                @after-leave="visible = false"
                                enter-active-class="transform transition ease-out duration-200"
                                :enter-from-class="offscreenClass"
                                enter-to-class="translate-x-0 translate-y-0"
                                leave-active-class="transform transition ease-in duration-150"
                                leave-from-class="translate-x-0 translate-y-0"
                                :leave-to-class="offscreenClass">
                        <!-- The bottom sheet gets an explicit dynamic-viewport
                             height (85dvh) below sm so it stands nearly full-height
                             with a top gap, and its inner h-full content resolves
                             against a real height. dvh (not vh) accounts for mobile
                             browser chrome so it does not overflow behind the
                             address bar. From sm up it is full height again
                             (sm:h-full) as the right slide-over. -->
                        <div    v-if="show"
                                :class="[
                                    width,
                                    mobileBottomSheet ? 'h-[85dvh] w-full sm:h-full sm:w-screen' : 'w-screen',
                                ]">
                            <!-- overflow-y-auto, not -scroll: `scroll` always
                                 reserves the scrollbar gutter even when the
                                 content fits, leaving an empty strip down the
                                 right edge. `auto` only shows the bar (and its
                                 gutter) when the content actually overflows.

                                 The bottom sheet rounds its top corners and clips
                                 to them below sm; the right slide-over stays
                                 square. -->
                            <!-- The bottom sheet gets a top border and an upward
                                 shadow to lift its rounded top edge off the page
                                 peeking behind it; the right slide-over keeps the
                                 plain shadow-xl. Both revert to shadow-xl from sm
                                 up. -->
                            <!-- The container scrolls; its top padding is dropped
                                 (pt-0) so the sticky header below can pin flush to
                                 the very top with nothing showing above it. The
                                 header carries its own top padding instead. -->
                            <div    class="h-full flex flex-col space-y-6 pb-6 bg-white overflow-y-auto"
                                    :class="mobileBottomSheet
                                        ? 'rounded-t-2xl border-t border-gray-200 [box-shadow:0_-8px_24px_-8px_rgba(0,0,0,0.18)] sm:rounded-none sm:border-t-0 sm:shadow-xl'
                                        : 'shadow-xl'"
                                    ref="modalContainer"
                                    @scroll="updateScroll"
                                    @click="cancelClose">
                                <!-- Sticky header: the title + close button pin to
                                     the top of the scrolling sheet, on a solid white
                                     backing so content scrolls underneath rather than
                                     showing through. Its own pt matches the padding
                                     removed from the container, and the rounded-top
                                     corners are repeated here so the sticky backing
                                     follows the sheet's rounded top on mobile.
                                     z-10 keeps it above the scrolling content. -->
                                <header class="sticky top-0 z-10 bg-white px-4 pt-6 sm:px-6"
                                        :class="mobileBottomSheet ? 'rounded-t-2xl sm:rounded-none' : ''">
                                    <div class="flex items-start justify-between space-x-3">
                                        <div class="w-full pb-5 border-b border-gray-200 space-y-2">
                                            <p class="text-lg leading-6 font-medium text-gray-900">
                                                <slot name="title">

                                                </slot>
                                            </p>
                                            <p class="max-w-4xl text-sm leading-5 text-gray-500">
                                                <slot name="title-content">
                                                </slot>
                                            </p>
                                        </div>
                                        <div class="h-7 flex items-center">
                                            <button
                                                    @click.stop="closeModal"
                                                    aria-label="Close panel"
                                                    class="text-gray-400 hover:text-gray-500">
                                                <tabler-icon name="x" size-class="h-6 w-6" :stroke-width="2"></tabler-icon>
                                            </button>
                                        </div>
                                    </div>
                                </header>
                                <div class="relative flex-1 px-4 sm:px-6">
                                    <!-- Replace with your content -->
                                    <div class="absolute inset-0 px-4 sm:px-6">
                                        <slot name="content">

                                        </slot>
                                    </div>
                                    <!-- /End replace -->
                                </div>
                            </div>
                        </div>
                        </transition>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import TablerIcon from "@/components/elements/TablerIcon.vue"
    export default {
        name: "SlideOverPanel",
        components: {TablerIcon},
        props:{
            show: {
                type: Boolean,
                default: false,
            },
            width: {
                type: String,
                default: 'max-w-2xl',
            },
            zIndex: {
                type: String,
                default: 'z-10',
            },
            // Opt in to a bottom-sheet presentation on phones: below sm the panel
            // comes UP from the bottom, full width, with a top gap and rounded top
            // corners. From sm up it is the normal right-edge slide-over. Off by
            // default so every existing consumer keeps the right slide-over.
            mobileBottomSheet: {
                type: Boolean,
                default: false,
            },
            scrollToY: {
                type: Number,
                default: 0,
            }
        },
        computed: {
            // The offscreen transform used for both enter-from and leave-to, so
            // the panel starts/ends offscreen in the direction that matches its
            // anchor. For the right slide-over that is always translate-x-full.
            // For the bottom sheet it is a scoped class that translates DOWN below
            // sm (slides up) and switches to translate-x-full from sm up (slides in
            // from the right), so one transition covers both breakpoints.
            offscreenClass() {
                return this.mobileBottomSheet ? 'panel-offscreen-sheet' : 'translate-x-full'
            },
        },
        data() {
            return {
                shouldClose: true,
                // Controls the overlay's v-show. It must stay true for the
                // whole open/close cycle: on open it flips true immediately so
                // the panel's slide-IN can play, and on close it stays true
                // until the panel's slide-OUT finishes (@after-leave sets it
                // false). If the overlay hid the instant `show` went false, it
                // would display:none the parent and cut off the leave animation.
                visible: this.show,
            }
        },
        methods: {
            cancelClose() {
                this.shouldClose = false
                setTimeout(() => {
                    this.shouldClose = true
                }, 100)
            },
            closeModal() {
                if (!this.shouldClose) {
                    return
                }
                setTimeout(() => {
                    if (this.shouldClose) {
                        this.$emit('update:show', false)
                    }
                }, 10)
            },

            updateScroll(ev) {
                this.$emit('update:scrollToY', 1)
            }
        },
        watch: {
            show(value) {
                // Open: reveal the overlay immediately so the panel can slide
                // in. Close: leave the overlay up -- @after-leave hides it once
                // the slide-out animation has finished.
                if (value) {
                    this.visible = true
                }

                if (!this.shouldClose) {
                    this.$emit('update:show', true)
                }
            },
            scrollToY() {
                this.$refs.modalContainer.scrollTo(0, this.scrollToY)
                this.$emit('update:scrollToY', 1)
            },
        },
    }
</script>

<style scoped>
/*
 * Bottom-sheet offscreen position. Below sm the panel sits fully below the
 * viewport (translateY 100%) so it slides UP into place; from sm up it sits off
 * the right edge (translateX 100%) so it slides in from the right like the normal
 * slide-over. Used as the transition's enter-from and leave-to state, so open and
 * close animate in the matching direction at each breakpoint. 639px is the pixel
 * equivalent of Tailwind's sm breakpoint (min-width: 640px).
 */
@media (max-width: 639px) {
    .panel-offscreen-sheet {
        transform: translateY(100%);
    }
}

@media (min-width: 640px) {
    .panel-offscreen-sheet {
        transform: translateX(100%);
    }
}
</style>